import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UtilsService } from './utils.service';

export interface ColumnMapping {
  id: number;
  file_type?: number;
  source_column: string;
  target_field: string;
  required: boolean;
}

export interface FileType {
  id: number;
  name: string;
  file_format: string;
  content_type: string;
  description: string | null;
  csv_delimiter: string;
  default_values: Record<string, unknown>;
  column_mappings: ColumnMapping[];
}

export type FileTypePayload = Omit<FileType, 'id' | 'column_mappings'>;
export type ColumnMappingPayload = Omit<ColumnMapping, 'id'> & { file_type: number };

@Injectable({ providedIn: 'root' })
export class FilesService {
  private httpClient = inject(HttpClient);
  private utilsService = inject(UtilsService);

  getFileTypes(): Observable<FileType[]> {
    return this.httpClient.get<FileType[]>(
      this.utilsService.buildApiUrl('files/file-types/')
    );
  }

  createFileType(data: FileTypePayload): Observable<{ id: number }> {
    return this.httpClient.post<{ id: number }>(
      this.utilsService.buildApiUrl('files/file-types/'),
      data
    );
  }

  updateFileType(id: number, data: Partial<FileTypePayload>): Observable<{ id: number }> {
    return this.httpClient.patch<{ id: number }>(
      this.utilsService.buildApiUrl(`files/file-types/${id}/`),
      data
    );
  }

  deleteFileType(id: number): Observable<void> {
    return this.httpClient.delete<void>(
      this.utilsService.buildApiUrl(`files/file-types/${id}/`)
    );
  }

  createColumnMapping(data: ColumnMappingPayload): Observable<ColumnMapping> {
    return this.httpClient.post<ColumnMapping>(
      this.utilsService.buildApiUrl('files/column-mappings/'),
      data
    );
  }

  updateColumnMapping(id: number, data: Partial<Omit<ColumnMapping, 'id' | 'file_type'>>): Observable<ColumnMapping> {
    return this.httpClient.patch<ColumnMapping>(
      this.utilsService.buildApiUrl(`files/column-mappings/${id}/`),
      data
    );
  }

  deleteColumnMapping(id: number): Observable<void> {
    return this.httpClient.delete<void>(
      this.utilsService.buildApiUrl(`files/column-mappings/${id}/`)
    );
  }

  processFile(fileUploadId: number): Observable<unknown> {
    return this.httpClient.post(
      this.utilsService.buildApiUrl(`upload/file/${fileUploadId}/process/`),
      {}
    );
  }

  uploadFile(file: File, fileTypeId: number, processNow = false): Observable<unknown> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileTypeId.toString());
    formData.append('process_now', processNow.toString());
    return this.httpClient.post(
      this.utilsService.buildApiUrl('upload/file/'),
      formData
    );
  }
}
